import { useEffect } from "react";

import { track } from "@/lib/analytics";

const DEPTHS = [25, 50, 75, 100] as const;

/**
 * `scroll_depth {page, depth}` — at most once per depth per page.
 *
 * Two deliberate choices:
 *
 *  1. A page that does not scroll (content shorter than the viewport) reports
 *     NOTHING. Firing all four depths for a page nobody could scroll would make
 *     "100" mean two different things, and the insight is meant to answer "of
 *     the visitors who could scroll, how far did they get".
 *  2. The measurement re-arms on route change, because `page` is the effect's
 *     dependency. So it is once per depth per PAGE, not per browser session.
 *
 * A page can also become scrollable after mount — this site lazy-loads every
 * route module, so the first layout is usually shorter than the final one.
 * A ResizeObserver on the document element catches that; scroll and resize
 * listeners catch everything else. All three are passive.
 */
export function useScrollDepth(page: string): void {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const fired = new Set<number>();
    let frame = 0;
    let detached = false;

    const measure = () => {
      frame = 0;
      if (detached) return;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const reached =
        ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      for (const depth of DEPTHS) {
        // Half a percent of slack: a page bottom is rarely an exact 100.
        if (reached >= depth - 0.5 && !fired.has(depth)) {
          fired.add(depth);
          track("scroll_depth", { page, depth });
        }
      }
      if (fired.size === DEPTHS.length) detach();
    };

    const schedule = () => {
      if (frame || detached) return;
      frame = window.requestAnimationFrame(measure);
    };

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(schedule);

    function detach() {
      if (detached) return;
      detached = true;
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
    }

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    observer?.observe(document.documentElement);
    schedule();

    return detach;
  }, [page]);
}
